import {lazy} from 'react'
import {Navigate, Route, Routes} from 'react-router-dom'
import {isFeatureEnabled} from '../../_gori/helpers'
import UseRoutesPermission from '../../_gori/hooks/UseRoutesPermission'
import {MasterLayout} from '../../_gori/layout/MasterLayout'
import {SuspenseView} from './AppRoutes'
import {FEATURES, ROUTES} from '../../_gori/constants'
import {useAuth} from '../modules/auth'
import {isActiveEnterpriseManager, isEnterpriseOwner} from '../modules/auth/core/userHelpers'

const PrivateRoutes = () => {
  const DashboardWrapper = lazy(() => import('../pages/dashboard/DashboardWrapper'))
  const OrdersWrapper = lazy(() => import('../pages/orders/OrdersWrapper'))
  const ShipmentsWrapper = lazy(() => import('../pages/shipments/ShipmentsWrapper'))
  const EndOfDayWrapper = lazy(() => import('../pages/end-of-day/EndOfDayWrapper'))
  const BatchesWrapper = lazy(() => import('../pages/batches/BatchesWrapper'))
  const A1Wrapper = lazy(() => import('../pages/a1/A1Wrapper'))
  const ClaimsWrapper = lazy(() => import('../pages/claims/ClaimsWrapper'))
  const ReportsWrapper = lazy(() => import('../pages/reports/ReportsWrapper'))
  const DepositWrapper = lazy(() => import('../pages/deposit/DepositWrapper'))
  const SettingsWrapper = lazy(() => import('../pages/settings/SettingsWrapper'))
  const SupportWrapper = lazy(() => import('../pages/support/SupportWrapper'))
  const AdminWrapper = lazy(() => import('../pages/admin/AdminWrapper'))
  const OrganizationsWrapper = lazy(() => import('../pages/organizations/OrganizationsWrapper'))
  const {routes, firstPage} = UseRoutesPermission()
  const {currentUser} = useAuth()
  const canAccessOrganizations =
    isFeatureEnabled(FEATURES.ORGANIZATIONS) &&
    (isActiveEnterpriseManager(currentUser) || isEnterpriseOwner(currentUser))

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {isFeatureEnabled(FEATURES.DASHBOARD) && routes.DASHBOARD.hasPermission && (
          <Route
            path={ROUTES.DASHBOARD.BASE}
            element={
              <SuspenseView>
                <DashboardWrapper />
              </SuspenseView>
            }
          />
        )}
        {routes.ORDERS_VIEW.hasPermission && (
          <Route
            path={ROUTES.ORDERS.RELATIVE.BASE}
            element={
              <SuspenseView>
                <OrdersWrapper />
              </SuspenseView>
            }
          />
        )}
        {routes.SHIPMENTS_VIEW.hasPermission && (
          <Route
            path={ROUTES.SHIPMENTS.RELATIVE.BASE}
            element={
              <SuspenseView>
                <ShipmentsWrapper />
              </SuspenseView>
            }
          />
        )}
        {routes.SHIPMENTS_VIEW.hasPermission && (
          <Route
            path={ROUTES.END_OF_DAY.RELATIVE.BASE}
            element={
              <SuspenseView>
                <EndOfDayWrapper />
              </SuspenseView>
            }
          />
        )}
        {routes.BATCHES_VIEW.hasPermission && (
          <Route
            path={ROUTES.BATCHES.RELATIVE.BASE}
            element={
              <SuspenseView>
                <BatchesWrapper />
              </SuspenseView>
            }
          />
        )}
        <Route
          path={ROUTES.A1.RELATIVE.BASE}
          element={
            <SuspenseView>
              <A1Wrapper />
            </SuspenseView>
          }
        />
        {routes.CLAIMS_VIEW.hasPermission && (
          <Route
            path={ROUTES.CLAIMS.RELATIVE.BASE}
            element={
              <SuspenseView>
                <ClaimsWrapper />
              </SuspenseView>
            }
          />
        )}
        {
          <Route
            path={ROUTES.REPORTS.RELATIVE.BASE}
            element={
              <SuspenseView>
                <ReportsWrapper />
              </SuspenseView>
            }
          />
        }
        {isFeatureEnabled(FEATURES.PAYMENT) && routes.DEPOSITS.hasPermission && (
          <Route
            path={ROUTES.DEPOSITS.BASE}
            element={
              <SuspenseView>
                <DepositWrapper />
              </SuspenseView>
            }
          />
        )}
        <Route
          path={ROUTES.SETTINGS.RELATIVE.BASE}
          element={
            <SuspenseView>
              <SettingsWrapper />
            </SuspenseView>
          }
        />
        {routes.SUPPORTS.hasPermission && (
          <Route
            path={ROUTES.SUPPORT.RELATIVE.BASE}
            element={
              <SuspenseView>
                <SupportWrapper />
              </SuspenseView>
            }
          />
        )}
        {routes.SYSTEM_ADMIN.hasPermission && (
          <Route
            path='admin/*'
            element={
              <SuspenseView>
                <AdminWrapper />
              </SuspenseView>
            }
          />
        )}
        {canAccessOrganizations && (
          <Route
            path={ROUTES.ORGANIZATIONS.BASE}
            element={
              <SuspenseView>
                <OrganizationsWrapper />
              </SuspenseView>
            }
          />
        )}
        {/* Redirect to Dashboard for all pages that are not available */}
        <Route path='*' element={<Navigate to={firstPage?.route ?? ROUTES.SUPPORT.BASE} />} />
      </Route>
    </Routes>
  )
}

export {PrivateRoutes}
